/**
 * 公用网关请求工具
 * 统一处理API请求、响应拦截、错误处理
 */

import { t } from "./i18n";
import { clearAuthSessionAndNotify } from "./authSession";

// API基础配置
const config = {
    // 统一走相对路径 /api 触发 package.json 的 proxy 代理
    baseURL: process.env.REACT_APP_API_BASE_URL || '/api', 
    timeout: 30000,
};

/**
 * 请求拦截器 - 在发送请求前处理
 * @param {Object} options - 请求配置
 * @returns {Object} - 处理后的请求配置
 */
/** 业务或 HTTP 层表示未登录 / token 无效 */
const isUnauthorizedCode = (data) => {
    if (!data || data.code === undefined || data.code === null) return false;
    const c = data.code;
    const n = typeof c === "string" ? parseInt(c, 10) : Number(c);
    return n === 401;
};

/**
 * 部分接口在 HTTP 200 下返回 success:false，code 不是 401，但 message 为「Token无效」等。
 * 若不识别，前端仍认为已登录并继续带旧 token，用户会看到生硬的「Token无效」。
 */
const isAuthFailureMessage = (data) => {
    if (!data || typeof data !== "object") return false;
    const msg = String(data.message ?? data.msg ?? "")
        .trim()
        .toLowerCase();
    if (!msg) return false;
    const hints = [
        "token无效",
        "token invalid",
        "invalid token",
        "token过期",
        "token expired",
        "expired token",
        "请先登录",
        "未登录",
        "登录已失效",
        "登录失效",
        "登录过期",
        "not logged in",
        "unauthorized",
    ];
    return hints.some(h => msg.includes(h));
};

const shouldInvalidateSession = (data) =>
    isUnauthorizedCode(data) || isAuthFailureMessage(data);

const sessionExpiredUserMessage = () =>
    t("request.error.sessionExpired", "登录已失效，请重新登录");

/**
 * 统一请求错误文案（全局公共方法）
 * - 优先按 HTTP 状态码区分
 * - 保留服务端可读 message（可选）
 */
export const getRequestErrorMessage = (error, options = {}) => {
    const {
        timeoutKey = "request.error.timeout",
        timeoutFallback = "Request timed out, please try again",
        networkKey = "request.error.network",
        networkFallback = "Network error, please check your connection and try again",
        serverExceptionKey = "request.error.serverException",
        serverExceptionFallback = "Server is busy, please try again later",
        requestExceptionKey = "request.error.requestException",
        requestExceptionFallback = "Request failed, please try again later",
        useServerMessage = true,
    } = options;

    const status = Number(error?.status);

    if (status === 408 || status === 504 || error?.code === "TIMEOUT") {
        return t(timeoutKey, timeoutFallback);
    }

    if (
        (!status && error?.message === "Failed to fetch") ||
        error?.code === "NETWORK_ERROR"
    ) {
        return t(networkKey, networkFallback);
    }

    if (status >= 500) {
        return t(serverExceptionKey, serverExceptionFallback);
    }

    if (useServerMessage && typeof error?.message === "string" && error.message.trim()) {
        return error.message;
    }

    return t(requestExceptionKey, requestExceptionFallback);
};

const requestInterceptor = (options) => {
    // 获取token（如果有）
    const token = localStorage.getItem('token');
    
    // 设置默认headers（FormData 不设 Content-Type，由浏览器自动带 boundary）
    const headers = {
        ...options.headers,
    };
    if (!(options.body instanceof FormData)) {
        headers['Content-Type'] = headers['Content-Type'] || 'application/json';
    }

    // 语言头：所有请求在 Header 中携带语言
    // - 若页面上手动选择过语言，则优先使用 localStorage.language（english / russian / chinese_traditional）
    //   并映射为标准语言码；
    // - 否则回退为浏览器语言（navigator.language）。
    try {
        let langHeader = null;
        if (typeof window !== "undefined") {
            const selected = window.localStorage.getItem("language");
            if (selected) {
                // 与 LANGUAGE_OPTIONS 中的 id 对应
                if (selected === "english") langHeader = "en-US";
                else if (selected === "russian") langHeader = "ru-RU";
                else if (
                  selected === "chinese_traditional" ||
                  selected === "chinese_simplified"
                ) {
                  langHeader = "zh-TW";
                }
            }
            if (!langHeader) {
                langHeader =
                    window.navigator.language ||
                    window.navigator.userLanguage ||
                    null;
            }
        }
        if (langHeader) {
            headers["Accept-Language"] = langHeader;
        }
    } catch (e) {
        // 语言解析失败时忽略，不影响请求发送
    }
    
    // 如果有token，添加到请求头
    if (token) {
        headers['Authorization'] = token;
    }
    
    return {
        ...options,
        headers,
    };
};

/**
 * 响应拦截器 - 处理响应数据
 * @param {Response} response - fetch响应对象
 * @returns {Promise} - 处理后的响应数据
 */
const responseInterceptor = async (response) => {
    // 不依赖 Content-Type：网关/中间层常把 JSON 标成 text/plain 或漏标，导致误判「非 JSON」。
    // 先读文本再 JSON.parse，才能稳定拿到后端的 message / code。
    const rawText = await response.text();
    const trimmed = rawText.replace(/^\uFEFF/, "").trim();
    let data;
    if (!trimmed) {
        const error = new Error(
            response.status === 404
                ? "API endpoint not found, please check API configuration"
                : `Server returned an empty response (${response.status})`,
        );
        error.status = response.status;
        error.code = "INVALID_RESPONSE";
        throw error;
    }
    try {
        data = JSON.parse(trimmed);
    } catch (e) {
        const error = new Error(
            response.status === 404
                ? "API endpoint not found, please check API configuration"
                : `Server returned a non-JSON response (${response.status})`,
        );
        error.status = response.status;
        error.code = "INVALID_RESPONSE";
        throw error;
    }
    if (typeof data !== "object" || data === null) {
        const error = new Error("Invalid server response format");
        error.status = response.status;
        error.code = "INVALID_RESPONSE";
        throw error;
    }
    
    // 根据HTTP状态码处理
    if (!response.ok) {
        // 处理特定状态码
        switch (response.status) {
            case 401:
                // 未授权：清 token 并由 App 打开登录弹窗
                clearAuthSessionAndNotify();
                console.warn('用户未授权，请重新登录');
                break;
            case 403:
                console.warn('无权限访问');
                break;
            case 404:
                console.warn('请求的资源不存在');
                break;
            case 500:
                console.warn('服务器内部错误');
                break;
            default:
                break;
        }
        if (response.status !== 401 && shouldInvalidateSession(data)) {
            clearAuthSessionAndNotify();
        }

        const httpAuthFail =
            response.status === 401 || shouldInvalidateSession(data);
        // 抛出错误，包含服务器返回的信息
        const error = new Error(
            httpAuthFail
                ? sessionExpiredUserMessage()
                : data.message || data.msg || "Request failed",
        );
        error.code = data.code;
        error.data = data;
        error.status = response.status;
        throw error;
    }
    
    // 业务逻辑错误处理
    // 优先检查 success 字段，其次检查 code 字段
    if (data.success === false) {
        const authFail = shouldInvalidateSession(data);
        if (authFail) {
            clearAuthSessionAndNotify();
        }
        const error = new Error(
            authFail
                ? sessionExpiredUserMessage()
                : data.message || data.msg || "Operation failed",
        );
        error.code = data.code;
        error.data = data;
        throw error;
    }
    
    // 如果没有 success 字段，则检查 code 字段
    if (data.success === undefined && data.code !== undefined && data.code !== null && data.code !== 0 && data.code !== 200) {
        const authFail = shouldInvalidateSession(data);
        if (authFail) {
            clearAuthSessionAndNotify();
        }
        const error = new Error(
            authFail
                ? sessionExpiredUserMessage()
                : data.message || data.msg || "Operation failed",
        );
        error.code = data.code;
        error.data = data;
        throw error;
    }
    
    return data;
};

/**
 * 核心请求方法
 * @param {string} url - 请求URL
 * @param {Object} options - 请求配置
 * @returns {Promise} - 响应数据
 */
const request = async (url, options = {}) => {
    // 拼接完整URL
    const fullURL = url.startsWith('http') ? url : `${config.baseURL}${url}`;
    
    // 应用请求拦截器
    const interceptedOptions = requestInterceptor(options);
    
    // 设置超时
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), options.timeout || config.timeout);
    
    try {
        const response = await fetch(fullURL, {
            ...interceptedOptions,
            signal: controller.signal,
        });
        
        clearTimeout(timeoutId);
        
        // 应用响应拦截器
        return await responseInterceptor(response);
    } catch (error) {
        clearTimeout(timeoutId);

        // 处理超时错误
        if (error.name === 'AbortError') {
            const timeoutError = new Error('Request timeout');
            timeoutError.code = 'TIMEOUT';
            timeoutError.message = getRequestErrorMessage(timeoutError);
            throw timeoutError;
        }

        // 处理网络错误
        if (error.message === 'Failed to fetch') {
            const networkError = new Error('Network connection failed, please check your connection');
            networkError.code = 'NETWORK_ERROR';
            networkError.message = getRequestErrorMessage(networkError);
            throw networkError;
        }

        if (error && typeof error === "object") {
            error.message = getRequestErrorMessage(error);
        }
        throw error;
    }
};

/**
 * GET请求
 * @param {string} url - 请求URL
 * @param {Object} params - 查询参数
 * @param {Object} options - 其他配置
 * @returns {Promise}
 */
export const get = (url, params = {}, options = {}) => {
    // 构建查询字符串
    const queryString = Object.keys(params).length > 0
        ? '?' + new URLSearchParams(params).toString()
        : '';
    
    return request(`${url}${queryString}`, {
        method: 'GET',
        ...options,
    });
};

/**
 * POST请求
 * @param {string} url - 请求URL
 * @param {Object} data - 请求体数据
 * @param {Object} options - 其他配置
 * @returns {Promise}
 */
export const post = (url, data = {}, options = {}) => {
    return request(url, {
        method: 'POST',
        body: JSON.stringify(data),
        ...options,
    });
};

/**
 * PUT请求
 * @param {string} url - 请求URL
 * @param {Object} data - 请求体数据
 * @param {Object} options - 其他配置
 * @returns {Promise}
 */
export const put = (url, data = {}, options = {}) => {
    return request(url, {
        method: 'PUT',
        body: JSON.stringify(data),
        ...options,
    });
};

/**
 * DELETE请求
 * @param {string} url - 请求URL
 * @param {Object} data - 请求体数据
 * @param {Object} options - 其他配置
 * @returns {Promise}
 */
export const del = (url, data = {}, options = {}) => {
    return request(url, {
        method: 'DELETE',
        body: JSON.stringify(data),
        ...options,
    });
};

/**
 * 上传文件
 * @param {string} url - 上传URL
 * @param {FormData} formData - 表单数据
 * @param {Object} options - 其他配置
 * @returns {Promise}
 */
export const upload = (url, formData, options = {}) => {
    return request(url, {
        method: 'POST',
        body: formData,
        headers: {
            // 不手动设置 Content-Type，让浏览器自动附带 boundary
        },
        ...options,
    });
};

// 导出默认request方法和配置
export { request, config };

export default {
    get,
    post,
    put,
    del,
    upload,
    request,
    config,
};
